/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/BackgroundData/utils/validateBackgroundForm.ts.
 */

import type { FormField, FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import { getBackgroundStageDisplayFields } from "../../../../../../components/submission/backgroundStageFieldOrder";
import {
  isValidReviewerEmail,
  requiredDataReviewerCount,
  dataReviewerEmailKey,
  dataReviewerNameKey,
  type DataReviewDropdownOption,
} from "./backgroundDataReviewers";
import {
  BACKGROUND_GEO_SELECTION_FIELD,
  BACKGROUND_GEO_SPEC_FIELD,
  normalizeBackgroundGeoSpec,
} from "../../../../../../utils/backgroundGeographicalSpecificity";

const PAIRED_GROUPS: Record<string, string> = {
  density: "density_unit",
  uValue: "uValue_unit",
  compressiveStrength: "compressiveStrength_unit",
  tensileStrength: "tensileStrength_unit",
  temperature: "temperature_unit",
  diameter: "diameterUnit",
  coverage: "coverageUnit",
  thickness: "thicknessUnit",
};

const TIMELINE_FIELD_NAMES = new Set([
  "collectionStartYear",
  "collectionStartMonth",
  "collectionStartDay",
  "collectionEndYear",
  "collectionEndMonth",
  "collectionEndDay",
]);

const OPTIONAL_LIFECYCLE_CHECKBOX_NAMES = new Set(["a1", "a2", "a3"]);
const MANDATORY_LIFECYCLE_CHECKBOX_NAME = "a1_a3";

const SKIP_FIELD_NAMES = new Set([
  "dataCompleteness",
  "materialCode",
  "temperature",
  "dataReviewerName_01",
  "dataReviewerEmail_01",
  "dataReviewerName",
  "dataReviewerEmail",
  BACKGROUND_GEO_SPEC_FIELD,
  BACKGROUND_GEO_SELECTION_FIELD,
]);

/** Type definition — Background Form Errors. */
export type BackgroundFormErrors = Record<string, string>;

function isYes(value: unknown): boolean {
  const s = String(value ?? "").trim().toLowerCase();
  return s === "yes" || s === "y" || s === "true" || s === "1";
}

function fieldLabel(field: FormField): string {
  return String(field.label ?? field.name ?? "This field").trim();
}

function fieldHasValue(
  field: FormField,
  values: Record<string, string | File>
): boolean {
  if (field.type === "MATERIALS") {
    return String(values.materialId ?? "").trim() !== "";
  }

  const value = values[field.name];
  if (value instanceof File) return true;

  if (field.type === "FILE" || field.type === "IMAGE") {
    const urlKey = `${field.name}Url`;
    const urlValue = values[urlKey];
    if (typeof urlValue === "string" && urlValue.trim()) return true;
    if (typeof value === "string" && value.trim()) return true;
    return false;
  }

  if (field.type === "CHECKBOX") {
    return value === "true";
  }

  return String(value ?? "").trim() !== "";
}

function isRawMaterialTemplateField(field: FormField): boolean {
  return (
    field.type === "RAW_MATERIALS" ||
    /^rawMaterial(_Unit)?_\d+$/i.test(field.name) ||
    /^rawMaterialUnit_\d+$/i.test(field.name)
  );
}

function isFieldApplicable(
  field: FormField,
  values: Record<string, string | File>,
  enabledPairs: Record<string, boolean>
): boolean {
  if (!field.isEnabled) return false;
  if (SKIP_FIELD_NAMES.has(field.name)) return false;
  if (isRawMaterialTemplateField(field)) return false;

  const materialPropsYes = isYes(values.materialPropertiesAvailable);

  const pairedMainKey = Object.entries(PAIRED_GROUPS).find(
    ([, unitName]) => unitName === field.name
  )?.[0];
  if (pairedMainKey) {
    if (!materialPropsYes || !enabledPairs[pairedMainKey]) return false;
  }

  if (PAIRED_GROUPS[field.name]) {
    if (!materialPropsYes || !enabledPairs[field.name]) return false;
  }

  if (TIMELINE_FIELD_NAMES.has(field.name) && !isYes(values.timelineAvailable)) {
    return false;
  }

  if (field.type === "READONLY" || field.isEditable === false) {
    return false;
  }

  if (field.name === "subMaterialUnit" && !String(values.subMaterialId ?? "").trim()) {
    return false;
  }

  if (
    (field.name === "technologyId" || field.name === "manufacturerId") &&
    !String(values.materialId ?? "").trim()
  ) {
    return false;
  }

  return true;
}

function validateRawMaterials(
  values: Record<string, string | File>,
  rawMaterialGroups: number[]
): BackgroundFormErrors {
  const errors: BackgroundFormErrors = {};
  let hasCompleteRow = false;

  for (const groupIndex of rawMaterialGroups) {
    const key = `rawMaterial_${String(groupIndex).padStart(2, "0")}`;
    const qtyKey = `${key}_qty`;
    const unitKey = `rawMaterialUnit_${String(groupIndex).padStart(2, "0")}`;
    const name = String(values[key] ?? "").trim();
    const qty = String(values[qtyKey] ?? "").trim();
    const unit = String(values[unitKey] ?? "").trim();

    if (!name) continue;

    if (!qty) {
      errors[qtyKey] = "Quantity is required.";
    }
    if (!unit) {
      errors[unitKey] = "Unit is required.";
    }

    if (name && qty && unit) {
      hasCompleteRow = true;
    }
  }

  if (!hasCompleteRow) {
    const firstKey = `rawMaterial_${String(rawMaterialGroups[0] ?? 1).padStart(2, "0")}`;
    if (!errors[firstKey]) {
      errors[firstKey] =
        "Please select at least one raw material with quantity and unit.";
    }
  }

  return errors;
}

function validateGeographicalSpecificity(
  values: Record<string, string | File>
): BackgroundFormErrors {
  const geoSpec = normalizeBackgroundGeoSpec(values[BACKGROUND_GEO_SPEC_FIELD] ?? "global");
  const geoSelection = String(values[BACKGROUND_GEO_SELECTION_FIELD] ?? "").trim();

  if (geoSpec === "global" && !geoSelection) {
    return {
      [BACKGROUND_GEO_SELECTION_FIELD]: "Please select a geographic scope.",
    };
  }

  if (geoSpec === "state" && !geoSelection) {
    return {
      [BACKGROUND_GEO_SELECTION_FIELD]: "Please select a state.",
    };
  }
  return {};
}

function validateTimelineFields(
  values: Record<string, string | File>
): BackgroundFormErrors {
  if (!isYes(values.timelineAvailable)) return {};

  const errors: BackgroundFormErrors = {};
  const timelineRequired: [string, string][] = [
    ["collectionStartYear", "Data Collection Commencement Year"],
    ["collectionStartMonth", "Data Collection Commencement Month"],
    ["collectionEndYear", "Data Collection Ending Year"],
    ["collectionEndMonth", "Data Collection Ending Month"],
  ];

  for (const [key, label] of timelineRequired) {
    if (!String(values[key] ?? "").trim()) {
      errors[key] = `Please select ${label}.`;
    }
  }

  return errors;
}

/** A1-A3 is required; A1, A2, and A3 remain optional. */
function validateLifeCycleSelection(
  values: Record<string, string | File>
): BackgroundFormErrors {
  if (values[MANDATORY_LIFECYCLE_CHECKBOX_NAME] === "true") return {};
  return {
    dataCompleteness: "Please select A1-A3.",
    [MANDATORY_LIFECYCLE_CHECKBOX_NAME]: "A1-A3 is required.",
  };
}

function validateDataReviewers(
  values: Record<string, string | File>,
  reviewerGroups: number[],
  normalizedDataReview: string,
  dataReviewOptions: DataReviewDropdownOption[]
): BackgroundFormErrors {
  const errors: BackgroundFormErrors = {};
  const requiredCount = requiredDataReviewerCount(
    normalizedDataReview,
    dataReviewOptions
  );
  if (requiredCount <= 0) return errors;

  for (const groupIndex of reviewerGroups) {
    const nameKey = dataReviewerNameKey(groupIndex);
    const emailKey = dataReviewerEmailKey(groupIndex);
    const name = String(values[nameKey] ?? "").trim();
    const email = String(values[emailKey] ?? "").trim();

    if (!name) {
      errors[nameKey] = "Reviewer name is required.";
    }
    if (!email) {
      errors[emailKey] = "Reviewer email is required.";
    } else if (!isValidReviewerEmail(email)) {
      errors[emailKey] = "Please enter a valid email address.";
    }
  }

  return errors;
}

/** Type definition — Validate Background Form Input. */
export type ValidateBackgroundFormInput = {
  formData: FormDataResponse;
  values: Record<string, string | File>;
  enabledPairs: Record<string, boolean>;
  rawMaterialGroups: number[];
  reviewerGroups: number[];
  normalizedDataReview: string;
  dataReviewFieldOptions: DataReviewDropdownOption[];
};

/** Returns field-keyed validation errors. Empty object when valid. */
export function validateBackgroundForm({
  formData,
  values,
  enabledPairs,
  rawMaterialGroups,
  reviewerGroups,
  normalizedDataReview,
  dataReviewFieldOptions,
}: ValidateBackgroundFormInput): BackgroundFormErrors {
  const errors: BackgroundFormErrors = {};

  if (!String(values.materialId ?? "").trim()) {
    const materialField = getBackgroundStageDisplayFields(formData).find(
      (f) => f.type === "MATERIALS" || f.name === "materialId"
    );
    errors.materialId = materialField
      ? `Please select ${fieldLabel(materialField)}.`
      : "Please select Material.";
  } else {
    Object.assign(errors, validateRawMaterials(values, rawMaterialGroups));
  }

  const fields = getBackgroundStageDisplayFields(formData);

  for (const field of fields) {
    if (!field.isMandatory) continue;
    if (OPTIONAL_LIFECYCLE_CHECKBOX_NAMES.has(field.name)) continue;
    if (field.name === MANDATORY_LIFECYCLE_CHECKBOX_NAME) continue;
    if (!isFieldApplicable(field, values, enabledPairs)) continue;
    if (fieldHasValue(field, values)) continue;
    if (!errors[field.name]) {
      errors[field.name] = `${fieldLabel(field)} is required.`;
    }
  }

  Object.assign(errors, validateLifeCycleSelection(values));
  Object.assign(errors, validateGeographicalSpecificity(values));
  Object.assign(errors, validateTimelineFields(values));
  Object.assign(
    errors,
    validateDataReviewers(
      values,
      reviewerGroups,
      normalizedDataReview,
      dataReviewFieldOptions
    )
  );

  return errors;
}

/** Exported function — get First Background Form Error. */
export function getFirstBackgroundFormError(
  errors: BackgroundFormErrors
): string | null {
  const messages = Object.values(errors);
  return messages.length > 0 ? messages[0] : null;
}
